import { useMemo } from "react";
import { usePathname } from "next/navigation";

import { useBreadcrumbOverrides } from "@/breadcrumb";
import type { BreadcrumbItemData } from "@/components/breadcrumb-types";
import {
  findRouteChain,
  pathMatchesRoute,
  type Route,
} from "@/config/navigation";

function breadcrumbHref(route: Route, pathname: string): string | undefined {
  if (route.path.includes("[")) {
    return pathMatchesRoute(route.path, pathname) ? pathname : undefined;
  }
  return route.path;
}

export function useRouteBreadcrumbs(): BreadcrumbItemData[] {
  const pathname = usePathname() ?? "/";
  const overrides = useBreadcrumbOverrides();

  return useMemo(() => {
    const chain = findRouteChain(pathname);
    if (chain.length === 0) {
      return [{ label: "Home", href: "/" }];
    }

    return chain.map((route, index) => {
      const isLast = index === chain.length - 1;
      const href = isLast ? undefined : breadcrumbHref(route, pathname);
      const override = overrides[route.path];
      return {
        label: override?.label ?? route.name,
        ...(override?.iconName ? { iconName: override.iconName } : {}),
        ...(href ? { href } : {}),
      };
    });
  }, [pathname, overrides]);
}
